import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button, Card, Input, Alert, Loader, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

const BLANK = { name: '', url: '', description: '', is_active: true, events: '', application_id: null };

function HeaderActions({ isNew, row, saving, setField, save, remove }) {
  return (
    <Can module="calls" action="configure">
      <div className="flex items-center gap-2">
        {!isNew && (
          <>
            <label className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={!!row.is_active} onChange={(e) => setField('is_active', e.target.checked)} /> Active
            </label>
            <Button size="sm" variant="secondary" onClick={remove}><Trash2 size={14} className="mr-1 inline" /> Delete</Button>
          </>
        )}
        <Button size="sm" onClick={save} disabled={saving || !row.name || !row.url}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>
    </Can>
  );
}

export default function WebhookEndpointDetail() {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const { fetchApi } = useAppApi();

  const [row, setRow] = useState(isNew ? { ...BLANK } : null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [apps, setApps] = useState([]);

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    fetchApi('crud', { model: 'AppWebhookEndpoint', action: 'show', where: { id } })
      .then((res) => setRow(res.data || res))
      .catch((ex) => setErr(apiError(ex)))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchApi('applicationsList').then((res) => setApps(Array.isArray(res) ? res : [])).catch(() => []);
  }, []);

  const setField = (key, value) => setRow((r) => ({ ...r, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      if (isNew) {
        const res = await fetchApi('crud', { model: 'AppWebhookEndpoint', action: 'create', data: row });
        nav(`/app-webhook/${res.data.id || res.id}`, { replace: true });
      } else {
        await fetchApi('crud', { model: 'AppWebhookEndpoint', action: 'update', where: { id }, data: row });
      }
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete webhook "${row.name}"? This cannot be undone.`)) return;
    try {
      await fetchApi('crud', { model: 'AppWebhookEndpoint', action: 'delete', where: { id } });
      nav('/app-webhook-endpoint');
    } catch (ex) {
      setErr(apiError(ex));
    }
  };

  if (loading) return <PageShell><Loader text="Loading webhook…" /></PageShell>;
  if (!row) return <PageShell><Alert variant="error" title="Could not load">{err || 'Webhook not found'}</Alert></PageShell>;

  return (
    <PageShell width="narrow" className="space-y-3">
      <PageHeader
        backTo="/app-webhook-endpoint"
        backLabel="Webhooks"
        title={isNew ? 'New Webhook' : row.name}
        actions={(
          <HeaderActions isNew={isNew} row={row} saving={saving} setField={setField} save={save} remove={remove} />
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}

      <Card className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name" value={row.name || ''} onChange={(e) => setField('name', e.target.value)} />
          <Input label="URL" value={row.url || ''} onChange={(e) => setField('url', e.target.value)} />
          <Input label="Description" value={row.description || ''} onChange={(e) => setField('description', e.target.value)} />
          <Input label="Events" value={row.events || ''} onChange={(e) => setField('events', e.target.value)} />
        </div>
        {!isNew && <p className="text-xs text-secondary-500">Events should be comma-separated (e.g. call.completed, call.failed).</p>}
        <Select
          label="Application"
          options={[{ label: 'Company-wide (all apps)', value: '' }, ...apps.map(a => ({ label: a.name, value: a.id }))]}
          value={row.application_id || ''}
          onChange={(v) => setRow(r => ({ ...r, application_id: v || null }))}
          allowClear
          searchable
        />
      </Card>
    </PageShell>
  );
}
